import { setDefaultOptions } from 'date-fns';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import {
  i18n,
  loadLanguageAsync,
} from '@apps/main/modules/localization/localizationSetup';
import router from '@apps/main/modules/router/routerSetup';

import {
  initializeStatsig,
  trackPerformanceLegacy,
  trackPerformance,
  getExperiment,
  assignExperimentValue,
  assignMultiExperimentValue,
  markUnloadedExperimentsAsControl,
} from '@libs/feature-toggle';
import { getLocale } from '@libs/sembo-dates';
import { decodeHTML, getDomain } from '@libs/utils';

import App from './App.vue';
import { createLogger } from './modules/plugins/logger';
import './styles/main.scss';
import { logException } from './modules/plugins/logException';
import { registerPreloadErrorHandler } from './modules/plugins/preloadErrorHandler';
import { HOTEL_SELECTION_LIST_LENGTH_CONTROL_VARIANT } from './new-desktop/core/stores/criteria/hotelSelectionListLengthExperiment';

registerPreloadErrorHandler();

const languageLocale = getDomain.getLanguageLocale();

if (decodeHTML(location.href) !== location.href) {
  location.href = decodeHTML(location.href) ?? location.href;
}

trackPerformance();
trackPerformanceLegacy();

(async function (): Promise<void> {
  await initializeStatsig();

  const shouldShowAlternativeCard = getExperiment('hotel_selection_map').get(
    'should_show_alternative_card',
    false
  );
  assignExperimentValue('hotel_selection_map', shouldShowAlternativeCard);

  const shouldShowRatings = getExperiment('hotel_details_ratings_desktop').get(
    'should_show_ratings',
    false
  );
  assignExperimentValue('hotel_details_ratings_desktop', shouldShowRatings);

  const shouldShowPriceSwitcher = getExperiment(
    'hotel_selection_price_communication'
  ).get('should_show_price_switcher', false);
  assignExperimentValue(
    'hotel_selection_price_communication',
    shouldShowPriceSwitcher
  );

  const hotelSelectionListLengthMobileVariant = getExperiment(
    'hotel_selection_list_length_mobile'
  ).get(
    'hotel_selection_list_length',
    HOTEL_SELECTION_LIST_LENGTH_CONTROL_VARIANT
  );
  assignMultiExperimentValue(
    'hotel_selection_list_length_mobile',
    hotelSelectionListLengthMobileVariant
  );

  const hotelSelectionListLengthDesktopVariant = getExperiment(
    'hotel_selection_list_length_desktop'
  ).get(
    'hotel_selection_list_length',
    HOTEL_SELECTION_LIST_LENGTH_CONTROL_VARIANT
  );
  assignMultiExperimentValue(
    'hotel_selection_list_length_desktop',
    hotelSelectionListLengthDesktopVariant
  );

  const shouldUseNewImageOrdering = getExperiment('hotel_images_ordering').get(
    'should_use_new_image_ordering',
    false
  );
  assignExperimentValue('hotel_images_ordering', shouldUseNewImageOrdering);

  const shouldShowTripIsland = getExperiment(
    'trip_island_in_activity_selection'
  ).get('should_show_trip_island', false);
  assignExperimentValue(
    'trip_island_in_activity_selection',
    shouldShowTripIsland
  );
})().catch(() => {
  logException('Statsig experiment initialization failed');
  // Unblock consumers awaiting isExperimentAsync so they fall back to control
  // instead of hanging on a promise that never resolves.
  markUnloadedExperimentsAsControl();
});

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

const logger = createLogger();
app.use(logger);

app.use(router);
app.use(i18n);

app.mount('#app');

// NOTE: This loads sembo shared web components after app is mounted,
// to not delay first request to smart search.
window.addEventListener('load', () => {
  const scripts = [
    'https://sembo-inspire-apis.sembo.travel/share-component/latest/main.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-auth/latest/sembo-auth.js',
    'https://sembo-inspire-apis.sembo.travel/sembo-footer/latest/sembo-footer.js',
  ];

  scripts.forEach((src) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.body.appendChild(script);
  });
});

setDefaultOptions({
  locale: getLocale(languageLocale),
});

loadLanguageAsync(languageLocale).catch(() =>
  logException('Internationalization language module loading failed')
);
